// Generate downloadable instruction documents (PDF/DOCX) from issue resolutions

interface ExportData {
  issueTitle: string
  issueDescription: string
  d365Version: string
  d365Module: string
  category: string
  customerName?: string
  resolutionSummary: string
  steps: { order: number; instruction: string; expectedResult?: string }[]
  exportedBy: string
  exportDate: string
}

export function exportAsPDF(data: ExportData): void {
  const html = buildInstructionHTML(data)
  const printWindow = window.open('', '_blank')
  if (!printWindow) return
  printWindow.document.write(html)
  printWindow.document.close()
  printWindow.onload = () => printWindow.print()
}

export function exportAsDOCX(data: ExportData): void {
  // Use HTML-based DOCX — Word opens HTML files saved as .doc reliably
  const html = buildInstructionHTML(data)
  const blob = new Blob(['\ufeff' + html], { type: 'application/msword' })
  downloadBlob(blob, `${sanitizeFilename(data.issueTitle)}-instructions.doc`)
}

function buildInstructionHTML(data: ExportData): string {
  const stepsHtml = data.steps
    .map((s) => `
      <tr>
        <td style="padding:8px;border:1px solid #ddd;text-align:center;font-weight:bold;">${s.order}</td>
        <td style="padding:8px;border:1px solid #ddd;">${esc(s.instruction)}</td>
        <td style="padding:8px;border:1px solid #ddd;color:#666;">${s.expectedResult ? esc(s.expectedResult) : '—'}</td>
      </tr>`)
    .join('')

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(data.issueTitle)} - Instructions</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; max-width: 800px; margin: 40px auto; color: #333; line-height: 1.6; }
  h1 { color: #003C41; border-bottom: 2px solid #9CD6D6; padding-bottom: 8px; font-size: 22px; }
  h2 { color: #296267; font-size: 16px; margin-top: 24px; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; background: #f8fafa; padding: 12px; border-radius: 6px; margin: 16px 0; font-size: 13px; }
  .meta-item { display: flex; gap: 6px; }
  .meta-label { font-weight: 600; color: #003C41; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 13px; }
  th { background: #003C41; color: white; padding: 8px; text-align: left; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 11px; color: #999; }
  @media print { body { margin: 20px; } }
</style></head><body>
<h1>${esc(data.issueTitle)}</h1>
<p><strong>Resolution Instructions</strong></p>

<div class="meta">
  <div class="meta-item"><span class="meta-label">D365 Version:</span> ${esc(data.d365Version || 'N/A')}</div>
  <div class="meta-item"><span class="meta-label">Module:</span> ${esc(data.d365Module || 'N/A')}</div>
  <div class="meta-item"><span class="meta-label">Category:</span> ${esc(data.category || 'N/A')}</div>
  ${data.customerName ? `<div class="meta-item"><span class="meta-label">Customer:</span> ${esc(data.customerName)}</div>` : ''}
</div>

<h2>Problem Description</h2>
<p>${esc(data.issueDescription)}</p>

<h2>Resolution</h2>
<p>${esc(data.resolutionSummary)}</p>

${data.steps.length > 0 ? `
<h2>Step-by-Step Instructions</h2>
<table>
  <thead><tr><th style="width:50px">Step</th><th>Instruction</th><th style="width:200px">Expected Result</th></tr></thead>
  <tbody>${stepsHtml}</tbody>
</table>` : ''}

<div class="footer">
  <p>Generated by KayQ D365 AI Assistant | ${esc(data.exportDate)} | Prepared by: ${esc(data.exportedBy)}</p>
</div>
</body></html>`
}

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9 -]/g, '').replace(/\s+/g, '-').slice(0, 50)
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
